import React, { useState, useEffect, useCallback } from "react";
import { Post } from "@/core/types/postTypes";
import { usePublishPost, PublishStatus } from "./hooks/usePublishPost";
import { publishPostService, retryFailedPublishService } from "./services/publishingService";
import { apiClient } from "@/services/apiClient";
import { PostMediaPreview } from "./PostMediaPreview";
import { useAppStore } from "@/core/store/useAppStore";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { CheckIcon, CloseIcon, SpinnerIcon, RefreshIcon, ShareIcon, WarningIcon } from "@/components/icons";
import { PLATFORM_REGISTRY, getPlatformConfig, PlatformConfig } from "@/config/platforms";
import { isVideoCarousel } from "./videoCarousel";
import { TikTokPostConsentModal } from "./TikTokPostConsentModal";
import { TikTokPostOptions } from "./services/tiktokCreatorInfo";

interface PublishModalProps {
  post: Post;
  clientId: string;
  onClose: () => void;
}

interface SocialMediaStatus {
  facebook?: {
    connected: boolean;
    pages: Array<{
      id: string;
      pageName: string;
    }>;
  };
  instagram?: {
    connected: boolean;
    accounts: Array<{
      id: string;
      username: string;
    }>;
  };
  tiktok?: {
    connected: boolean;
    /**
     * True once TikTok has approved the Content Posting API audit and
     * TIKTOK_DIRECT_POST_ENABLED is on. Only then does a post go live and
     * require the consent screen; while false the video goes to the
     * account's drafts and the creator picks visibility in the TikTok app.
     */
    directPostEnabled?: boolean;
    accounts: Array<{
      id: string;
      displayName?: string;
      username?: string;
      openId?: string;
    }>;
  };
}

export const PublishModal: React.FC<PublishModalProps> = ({
  post,
  clientId,
  onClose,
}) => {
  const { getPublishingStatus } = usePublishPost();
  const [publishingStatus, setPublishingStatus] =
    useState<PublishStatus | null>(null);
  const [socialMediaStatus, setSocialMediaStatus] =
    useState<SocialMediaStatus | null>(null);
  const [statusLoadFailed, setStatusLoadFailed] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Per-platform state
  const [publishingPlatforms, setPublishingPlatforms] = useState<Set<string>>(
    new Set()
  );
  const [platformNotices, setPlatformNotices] = useState<Record<string, string>>(
    {}
  );
  const [platformErrors, setPlatformErrors] = useState<Record<string, string>>(
    {}
  );

  // Force-publish confirm scoped to a single platform
  const [showForceConfirm, setShowForceConfirm] = useState(false);
  const [forceConfirmPlatform, setForceConfirmPlatform] = useState<string | null>(null);

  // TikTok requires an explicit consent screen before any post that goes
  // live. When this is non-null the consent modal is open; `force` is
  // carried through so a re-publish still collects fresh consent.
  const [tiktokConsent, setTiktokConsent] = useState<{ force: boolean } | null>(
    null
  );

  // SSE-based publish progress from store
  const ssePublishProgress = useAppStore(
    (state) => state.realtime.publishProgress[post.id]
  );

  // Load social media connection status
  useEffect(() => {
    const loadSocialMediaStatus = async () => {
      try {
        const data = await apiClient.get<SocialMediaStatus>(
          `/api/social-media/status/${clientId}`
        );
        setSocialMediaStatus(data);
      } catch (error) {
        console.error("Failed to load social media status:", error);
        setStatusLoadFailed(true);
      }
    };

    loadSocialMediaStatus();
  }, [clientId]);

  // Load current publishing status
  useEffect(() => {
    const loadPublishingStatus = async () => {
      try {
        const status = await getPublishingStatus(post.id);
        setPublishingStatus(status);
      } catch (error) {
        console.error("Failed to load publishing status:", error);
      } finally {
        setIsLoadingStatus(false);
      }
    };

    loadPublishingStatus();
  }, [post.id, getPublishingStatus]);

  // Update local state when SSE provides publish progress
  useEffect(() => {
    if (!ssePublishProgress) return;

    const platforms: Record<string, any> = {};

    if (ssePublishProgress.platforms) {
      Object.entries(ssePublishProgress.platforms).forEach(([key, value]) => {
        const config = getPlatformConfig(key);
        const platformKey = config?.publishKey || key;
        platforms[platformKey] = {
          status: value.status,
          error_message: value.error,
        };
      });
    }

    let overallStatus: string = ssePublishProgress.status;
    if (overallStatus === "completed") overallStatus = "published";
    if (overallStatus === "partial") overallStatus = "partially_published";
    if (overallStatus === "error") overallStatus = "failed";

    setPublishingStatus((prev) => ({
      ...prev,
      overall_status: overallStatus as any,
      platforms:
        Object.keys(platforms).length > 0 ? platforms : prev?.platforms,
    } as PublishStatus));
  }, [ssePublishProgress]);

  // Clear stale publishingPlatforms when SSE reports terminal state
  useEffect(() => {
    if (!publishingStatus?.platforms) return;

    setPublishingPlatforms((prev) => {
      const next = new Set(prev);
      Array.from(prev).forEach((platformKey) => {
        const record = getPublishRecord(platformKey);
        if (
          record &&
          (record.status === "published" || record.status === "failed")
        ) {
          next.delete(platformKey);
        }
      });
      return next.size !== prev.size ? next : prev;
    });
  }, [publishingStatus]);

  const platformRecords = publishingStatus?.platforms || {};

  const getPublishRecord = useCallback(
    (platform: string) => {
      const config = getPlatformConfig(platform);
      if (!config) return undefined;
      const record = platformRecords[config.publishKey];
      if (record) return record;
      return Object.entries(platformRecords).find(([key]) =>
        key.startsWith(platform)
      )?.[1];
    },
    [platformRecords]
  );

  // The modal flips an async publish (TikTok, or any job-based platform)
  // from "publishing" to its terminal state off a single transient SSE
  // event. If that event is missed — a dropped realtime connection, a
  // gevent worker timeout, or Chrome's per-host EventSource cap — the card
  // sticks on "Publishing..." until a manual reload re-reads the DB. Poll
  // the status as a fallback while any platform is still non-terminal so
  // the modal reconciles itself without depending on SSE delivery.
  const hasInProgressRecord = Object.values(platformRecords).some(
    (r) => r?.status === "publishing" || r?.status === "pending"
  );

  useEffect(() => {
    if (!hasInProgressRecord) return;

    let polls = 0;
    const interval = setInterval(() => {
      polls += 1;
      getPublishingStatus(post.id).then((status) => {
        if (status) setPublishingStatus(status);
      });
      // Safety cap (~3 min): a record wedged in "publishing" server-side
      // must not poll forever if the user leaves the modal open.
      if (polls >= 45) clearInterval(interval);
    }, 4000);

    return () => clearInterval(interval);
  }, [hasInProgressRecord, post.id, getPublishingStatus]);

  const hasAnyConnectedPlatform = socialMediaStatus
    ? Object.keys(PLATFORM_REGISTRY).some((key) => {
        const status = socialMediaStatus[key as keyof SocialMediaStatus] as
          | { connected: boolean }
          | undefined;
        return status?.connected === true;
      })
    : false;

  const isPostReady = post.postFolderPreparationStatus === "ready";

  /**
   * Whether publishing to this platform lands in drafts rather than going
   * live. Static for Facebook/Instagram; for TikTok it follows the
   * deployment's Direct Post setting, so the wording stops saying "Drafts"
   * the moment the audit is approved and the flag is flipped.
   */
  const publishesAsDraft = useCallback(
    (platformKey: string, config: PlatformConfig) => {
      if (platformKey === "tiktok") {
        return !socialMediaStatus?.tiktok?.directPostEnabled;
      }
      return !!config.publishAsDraft;
    },
    [socialMediaStatus]
  );

  // --- Handlers ---

  const handlePublishPlatform = useCallback(
    async (
      platform: string,
      force = false,
      platformOptions?: Record<string, unknown>
    ) => {
      setPublishingPlatforms((prev) => new Set(prev).add(platform));
      setPlatformErrors((prev) => {
        const next = { ...prev };
        delete next[platform];
        return next;
      });
      setPlatformNotices((prev) => {
        const next = { ...prev };
        delete next[platform];
        return next;
      });
      setErrorMessage(null);

      try {
        const result = await publishPostService(
          post.id,
          clientId,
          [platform],
          force,
          platformOptions
        );

        if (!result.success) {
          setPlatformErrors((prev) => ({
            ...prev,
            [platform]:
              result.errors?.join(", ") || "Failed to publish",
          }));
        } else {
          if (result.queuedForPreparation) {
            setPlatformNotices((prev) => ({
              ...prev,
              [platform]:
                "Preparing media — the post will publish automatically in a few minutes.",
            }));
          }
          // Refresh status to pick up new records
          const status = await getPublishingStatus(post.id);
          setPublishingStatus(status);
        }
      } catch (error: any) {
        if (error.code === "ALREADY_PUBLISHED") {
          setForceConfirmPlatform(platform);
          setShowForceConfirm(true);
        } else {
          setPlatformErrors((prev) => ({
            ...prev,
            [platform]: error.message || "Failed to publish",
          }));
        }
      } finally {
        setPublishingPlatforms((prev) => {
          const next = new Set(prev);
          next.delete(platform);
          return next;
        });
      }
    },
    [post.id, clientId, getPublishingStatus]
  );

  const handleRetryPlatform = useCallback(
    async (platform: string) => {
      const record = getPublishRecord(platform);
      if (!record?.id) return;

      setPublishingPlatforms((prev) => new Set(prev).add(platform));
      setPlatformErrors((prev) => {
        const next = { ...prev };
        delete next[platform];
        return next;
      });

      try {
        await retryFailedPublishService(record.id, clientId, post.id);
        const status = await getPublishingStatus(post.id);
        setPublishingStatus(status);
      } catch (error: any) {
        setPlatformErrors((prev) => ({
          ...prev,
          [platform]: error.message || "Failed to retry",
        }));
      } finally {
        setPublishingPlatforms((prev) => {
          const next = new Set(prev);
          next.delete(platform);
          return next;
        });
      }
    },
    [getPublishRecord, clientId, post.id, getPublishingStatus]
  );

  /**
   * Entry point for every publish button.
   *
   * A TikTok post that goes live never posts straight from a click: the
   * Content Sharing Guidelines require a consent screen showing the
   * destination account, a visibility choice with no default, the
   * interaction toggles and the commercial-content disclosure first.
   *
   * A TikTok *draft* upload skips it. There is no visibility to choose —
   * the creator finishes the post in the TikTok app — and the consent
   * screen's data source (creator_info) needs the video.publish scope,
   * which we do not hold until the audit is approved. Gating drafts on it
   * would break the draft flow that works today.
   */
  const requestPublish = useCallback(
    (platform: string, force = false) => {
      if (platform === "tiktok") {
        // Unknown posting mode is treated as consent-required. Falling
        // through would publish without recorded consent, and if Direct
        // Post happens to be on that is the one thing the guidelines
        // forbid. The modal fetches its own state and can report the
        // problem itself.
        if (!socialMediaStatus || socialMediaStatus.tiktok?.directPostEnabled) {
          setTiktokConsent({ force });
          return;
        }
      }
      handlePublishPlatform(platform, force);
    },
    [handlePublishPlatform, socialMediaStatus]
  );

  const handleTiktokConsent = useCallback(
    (options: TikTokPostOptions) => {
      const force = tiktokConsent?.force ?? false;
      setTiktokConsent(null);
      handlePublishPlatform("tiktok", force, { tiktok: options });
    },
    [tiktokConsent, handlePublishPlatform]
  );

  const handleForcePublish = async () => {
    setShowForceConfirm(false);
    if (forceConfirmPlatform) {
      requestPublish(forceConfirmPlatform, true);
    }
    setForceConfirmPlatform(null);
  };

  const handleResetPlatform = async (platform: string) => {
    try {
      const record = getPublishRecord(platform);
      if (!record?.id) return;

      await apiClient.post(
        `/api/social-media/publish/reset/${record.id}`,
        {}
      );

      const status = await getPublishingStatus(post.id);
      setPublishingStatus(status);
    } catch (error: any) {
      console.error("Failed to reset platform:", error);
      setErrorMessage(error.message || "Failed to reset platform");
    }
  };

  // --- Per-platform card ---

  const renderPlatformCard = (platformKey: string, config: PlatformConfig) => {
    const connectionInfo =
      socialMediaStatus?.[platformKey as keyof SocialMediaStatus];
    const isConnected =
      (connectionInfo as { connected: boolean } | undefined)?.connected ??
      false;
    const record = getPublishRecord(platformKey);
    const isPlatformPublishing = publishingPlatforms.has(platformKey);
    const isPublished = record?.status === "published";
    const isFailed = record?.status === "failed";
    const isInProgress =
      record?.status === "publishing" ||
      record?.status === "pending" ||
      isPlatformPublishing;
    const platformError = platformErrors[platformKey];
    const IconComponent = config.iconComponent;
    const asDraft = publishesAsDraft(platformKey, config);

    // Facebook feed carousels cannot contain video children (Graph API
    // limitation), so publishing is unavailable for this post.
    const unavailableForPost =
      platformKey === "facebook" && isVideoCarousel(post)
        ? "Video carousels aren't supported on Facebook"
        : null;

    const accountName =
      (connectionInfo as any)?.pages?.[0]?.pageName ||
      (connectionInfo as any)?.accounts?.[0]?.displayName ||
      (connectionInfo as any)?.accounts?.[0]?.username ||
      null;

    const buttonsDisabled = !isPostReady || isPlatformPublishing;

    return (
      <div
        key={platformKey}
        className={`border rounded-lg p-4 ${
          !isConnected || unavailableForPost ? "opacity-50" : ""
        }`}
      >
        <div className="flex items-center justify-between">
          {/* Left: icon + name + account */}
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${config.iconBgClass}`}
            >
              <IconComponent
                className={`w-5 h-5 ${config.iconTextClass}`}
              />
            </div>
            <div>
              <h3 className="font-medium text-sm">{config.label}</h3>
              {accountName && (
                <p className="text-xs text-gray-500">@{accountName}</p>
              )}
              {!isConnected && (
                <p className="text-xs text-gray-400">Not connected</p>
              )}
            </div>
          </div>

          {/* Right: action button */}
          <div className="flex items-center gap-2">
            {!isConnected ? null : !config.canPublish ? (
              <span className="text-xs text-gray-400">
                {config.publishDisabledReason || "Publishing unavailable"}
              </span>
            ) : unavailableForPost ? (
              <span className="text-xs text-gray-400">
                {unavailableForPost}
              </span>
            ) : isInProgress ? (
              <Button variant="secondary" size="sm" disabled>
                <SpinnerIcon className="w-4 h-4 mr-1" />
                {asDraft ? "Sending to Drafts..." : "Publishing..."}
              </Button>
            ) : isPublished ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setForceConfirmPlatform(platformKey);
                    setShowForceConfirm(true);
                  }}
                  disabled={buttonsDisabled}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <RefreshIcon className="w-4 h-4 mr-1" />
                  {asDraft ? "Re-send to Drafts" : "Re-publish"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleResetPlatform(platformKey)}
                  className="text-red-600 hover:text-red-700"
                >
                  Reset
                </Button>
              </div>
            ) : isFailed ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleRetryPlatform(platformKey)}
                  disabled={buttonsDisabled}
                >
                  <RefreshIcon className="w-4 h-4 mr-1" />
                  Retry
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleResetPlatform(platformKey)}
                  className="text-red-600 hover:text-red-700"
                >
                  Reset
                </Button>
              </div>
            ) : (
              <Button
                variant="primary"
                size="sm"
                onClick={() => requestPublish(platformKey)}
                disabled={buttonsDisabled}
              >
                <ShareIcon className="w-4 h-4 mr-1" />
                {asDraft ? "Send to Drafts" : "Publish"}
              </Button>
            )}
          </div>
        </div>

        {/* Status line below */}
        {isConnected && (
          <div className="mt-2 ml-[52px]">
            {isPublished && (
              <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                <CheckIcon className="w-3.5 h-3.5" />
                {asDraft ? "Sent to Drafts" : "Published"}
                {record?.publishedAt && (
                  <span className="text-gray-400 ml-1">
                    {new Date(record.publishedAt).toLocaleString()}
                  </span>
                )}
              </span>
            )}
            {isFailed && !platformError && record?.errorMessage && (
              <p className="text-xs text-red-600">{record.errorMessage}</p>
            )}
            {platformError && (
              <p className="text-xs text-red-600">{platformError}</p>
            )}
            {platformNotices[platformKey] && (
              <p className="text-xs text-blue-600">
                {platformNotices[platformKey]}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Modal
        isOpen={true}
        onClose={onClose}
        title="Publish Post"
        size="xl"
        footer={
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        }
      >
        <div className="p-6">
          {/* Post Preview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="font-medium mb-3">Post Preview</h3>
              <PostMediaPreview post={post} clientId={clientId} />
            </div>

            <div>
              <h3 className="font-medium mb-3">Caption</h3>
              <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                <p className="text-sm whitespace-pre-wrap">
                  {post.caption || "No caption"}
                </p>
              </div>

              <div className="mt-4">
                <h3 className="font-medium mb-2">Details</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>Type: {post.postType}</p>
                  <p>Media: {post.mediaItems?.length || 0} items</p>
                  <p>
                    Status:{" "}
                    {isPostReady
                      ? "Ready"
                      : post.postFolderPreparationStatus || "Not prepared"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Connection status unavailable */}
          {statusLoadFailed && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <WarningIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">
                    Couldn&rsquo;t load connection status
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    Account names and platform availability may be wrong.
                    Reload before publishing.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* No connected accounts warning */}
          {socialMediaStatus && !hasAnyConnectedPlatform && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <WarningIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">
                    No social media accounts connected
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    Connect at least one social media account before publishing.
                    The client can connect accounts from their social media
                    setup page.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Preparation warning */}
          {!isPostReady && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <WarningIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">
                    Post not ready to publish
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    Post folder must be prepared first. Set post status to
                    &ldquo;Ready&rdquo; to start preparation.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Platform cards */}
          <div className="mb-6">
            <h3 className="font-medium mb-3">Platforms</h3>
            <div className="flex flex-col gap-3">
              {Object.entries(PLATFORM_REGISTRY).map(([key, config]) =>
                renderPlatformCard(key, config)
              )}
            </div>
          </div>

          {/* Non-platform-specific error */}
          {errorMessage && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{errorMessage}</p>
            </div>
          )}
        </div>
      </Modal>

      {tiktokConsent && (
        <TikTokPostConsentModal
          post={post}
          clientId={clientId}
          onCancel={() => setTiktokConsent(null)}
          onConfirm={handleTiktokConsent}
        />
      )}

      <ConfirmDialog
        isOpen={showForceConfirm}
        onClose={() => {
          setShowForceConfirm(false);
          setForceConfirmPlatform(null);
        }}
        onConfirm={handleForcePublish}
        title="Re-publish Post?"
        message={
          forceConfirmPlatform
            ? `This post has already been published to ${
                PLATFORM_REGISTRY[forceConfirmPlatform]?.label ||
                forceConfirmPlatform
              }. Re-publishing will create a duplicate post on the platform.`
            : "This post has already been published. Re-publishing will create a duplicate post on the platform."
        }
        confirmText="Re-publish"
        variant="warning"
        isLoading={publishingPlatforms.has(forceConfirmPlatform || "")}
      />
    </>
  );
};
